import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Presentation,
  FileText,
  Home,
  ChevronRight,
  FolderOpen,
  Loader,
  Download,
  CheckCircle,
  Layers,
  Zap,
  Shield
} from 'lucide-react';
import SharePointBrowser from '../components/SharePointBrowser';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/api.config';

export default function SlidesMaker() {
  const navigate = useNavigate();

  // SharePoint browser state
  const [sharePointBrowserOpen, setSharePointBrowserOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState<'file' | 'folder'>('file');
  const [selectedFile, setSelectedFile] = useState<{
    driveId: string;
    fileId: string;
    fileName: string;
    driveName: string;
  } | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<{
    driveId: string;
    folderId?: string;
    folderName: string;
    folderPath: string;
  } | null>(null);

  // Form state
  const [slidesPerPage, setSlidesPerPage] = useState<2 | 4>(4);
  const [generating, setGenerating] = useState(false);

  // Handle SharePoint file/folder selection
  const handleSharePointSelect = (driveId: string, driveName: string, folderPath?: string, folderId?: string, fileName?: string, fileId?: string) => {
    if (selectionMode === 'file' && fileName && fileId) {
      // File selection
      setSelectedFile({
        driveId,
        fileId,
        fileName,
        driveName: folderPath ? `${driveName}/${folderPath}` : driveName,
      });
      setSelectedFolder(null);
    } else if (selectionMode === 'folder') {
      // Folder selection
      setSelectedFolder({
        driveId,
        folderId,
        folderName: driveName,
        folderPath: folderPath ? `${driveName}/${folderPath}` : driveName,
      });
      setSelectedFile(null);
    }
    setSharePointBrowserOpen(false);
  };

  // Generate slides
  const handleGenerateSlides = async () => {
    if (!selectedFile && !selectedFolder) {
      Swal.fire('Error', 'Please select a file or folder from SharePoint first', 'error');
      return;
    }

    try {
      setGenerating(true);

      if (selectedFile) {
        // Single file generation
        const response = await fetch(`${API_BASE_URL}/slides/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fileId: selectedFile.fileId,
            driveId: selectedFile.driveId,
            fileName: selectedFile.fileName,
            slidesPerPage,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to generate slides' }));
          throw new Error(errorData.error || 'Failed to generate slides');
        }

        // Backend returns PDF directly as blob
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = selectedFile.fileName.replace('.docx', `_${slidesPerPage}_slides.pdf`);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        await Swal.fire({
          icon: 'success',
          title: 'Slides Generated!',
          text: 'PDF downloaded successfully. Redirecting to slides history...',
          confirmButtonText: 'OK',
          timer: 2000,
        });

        setSelectedFile(null);

        // Navigate to slides history
        navigate('/slides-history');
      } else if (selectedFolder) {
        // Folder batch generation
        const response = await fetch(`${API_BASE_URL}/slides/generate-batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            driveId: selectedFolder.driveId,
            folderId: selectedFolder.folderId,
            folderPath: selectedFolder.folderPath,
            slidesPerPage,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to generate slides for folder' }));
          throw new Error(errorData.error || 'Failed to generate slides for folder');
        }

        const result = await response.json();

        await Swal.fire({
          icon: 'success',
          title: 'Batch Generation Completed!',
          html: `Successfully generated slides for ${result.data?.successCount || 0} out of ${result.data?.totalFiles || 0} files.<br/>Redirecting to slides history...`,
          confirmButtonText: 'OK',
          timer: 2000,
        });

        setSelectedFolder(null);

        // Navigate to slides history
        navigate('/slides-history');
      }

      // Reset form
      setSlidesPerPage(4);

    } catch (error: any) {
      console.error('Error generating slides:', error);
      Swal.fire('Error', error.message || 'Failed to generate slides', 'error');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/dashboard" className="flex items-center hover:text-blue-600 transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-400">Slides</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-semibold">Slides Maker</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Presentation className="w-8 h-8" />
          Slides Maker
        </h1>
        <p className="mt-2 text-gray-600">
          Generate professional PDF slides from SharePoint syllabus files
        </p>
      </div>

      {/* Features Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-semibold">2 or 4 Slides</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">Per Page</p>
            </div>
            <Layers className="w-12 h-12 text-blue-600 opacity-50" />
          </div>
        </div>
        <div className="bg-orange-50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-semibold">FK Partners</p>
              <p className="text-2xl font-bold text-orange-900 mt-1">Branding</p>
            </div>
            <FileText className="w-12 h-12 text-orange-600 opacity-50" />
          </div>
        </div>
        <div className="bg-green-50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-semibold">Instant</p>
              <p className="text-2xl font-bold text-green-900 mt-1">Generation</p>
            </div>
            <Zap className="w-12 h-12 text-green-600 opacity-50" />
          </div>
        </div>
        <div className="bg-purple-50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-semibold">SharePoint</p>
              <p className="text-2xl font-bold text-purple-900 mt-1">Integration</p>
            </div>
            <Shield className="w-12 h-12 text-purple-600 opacity-50" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {!selectedFile && !selectedFolder ? (
          /* Initial State - File/Folder Selection */
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-12 h-12 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Select Your Syllabus from SharePoint
            </h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              Browse SharePoint and select a single file or complete folder to generate professional slides
            </p>

            {/* Selection Mode Toggle */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-lg border-2 border-gray-200 p-1 bg-gray-50">
                <button
                  onClick={() => setSelectionMode('file')}
                  className={`px-6 py-2 rounded-md font-semibold text-sm transition-all ${
                    selectionMode === 'file'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  📄 Single File
                </button>
                <button
                  onClick={() => setSelectionMode('folder')}
                  className={`px-6 py-2 rounded-md font-semibold text-sm transition-all ${
                    selectionMode === 'folder'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  📁 Complete Folder
                </button>
              </div>
            </div>

            <button
              onClick={() => setSharePointBrowserOpen(true)}
              className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold text-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl"
            >
              <FolderOpen className="w-6 h-6" />
              {selectionMode === 'file' ? 'Browse SharePoint Files' : 'Browse SharePoint Folders'}
            </button>

            {/* Instructions */}
            <div className="mt-12 p-6 bg-gray-50 rounded-lg text-left max-w-2xl mx-auto">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                How to Use:
              </h3>
              <ol className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span>Choose between <strong>Single File</strong> or <strong>Complete Folder</strong> mode</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span>Click the button above to browse SharePoint</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span>Navigate and select your DOCX file or folder containing DOCX files</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <span>Choose your preferred layout (2 or 4 slides per page)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                  <span>Click "Generate Slides" button</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">6</span>
                  <span>{selectionMode === 'file' ? 'PDF will download automatically!' : 'Batch processing will start - check Generated Slides page for status'}</span>
                </li>
              </ol>
            </div>
          </div>
        ) : (
          /* File/Folder Selected State */
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Selected File/Folder Card */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-green-900 text-lg">
                      {selectedFile ? '📄 File Selected' : '📁 Folder Selected'}
                    </h3>
                  </div>
                  <div className="space-y-1 text-sm">
                    {selectedFile ? (
                      <>
                        <p className="text-gray-700">
                          <span className="font-semibold">File:</span>{' '}
                          <span className="text-gray-900">{selectedFile.fileName}</span>
                        </p>
                        <p className="text-gray-700">
                          <span className="font-semibold">Location:</span>{' '}
                          <span className="text-gray-600">{selectedFile.driveName}</span>
                        </p>
                      </>
                    ) : selectedFolder ? (
                      <>
                        <p className="text-gray-700">
                          <span className="font-semibold">Folder:</span>{' '}
                          <span className="text-gray-900">{selectedFolder.folderPath}</span>
                        </p>
                        <p className="text-gray-600 text-xs mt-2">
                          ℹ️ All .docx files in this folder will be processed
                        </p>
                      </>
                    ) : null}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      setSelectedFolder(null);
                    }}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {selectedFile ? 'Change File' : 'Change Folder'}
                  </button>
                </div>
              </div>
            </div>

            {/* Slides Per Page Selector */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <label className="block text-sm font-bold text-gray-900 mb-4">
                Slides Per Page
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setSlidesPerPage(2)}
                  className={`p-6 rounded-lg font-bold text-lg transition-all ${
                    slidesPerPage === 2
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Layers className={`w-8 h-8 ${slidesPerPage === 2 ? 'text-white' : 'text-gray-400'}`} />
                    <span>2 Slides</span>
                  </div>
                </button>

                <button
                  onClick={() => setSlidesPerPage(4)}
                  className={`p-6 rounded-lg font-bold text-lg transition-all ${
                    slidesPerPage === 4
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Layers className={`w-8 h-8 ${slidesPerPage === 4 ? 'text-white' : 'text-gray-400'}`} />
                    <span>4 Slides</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerateSlides}
              disabled={generating}
              className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-green-600 text-white rounded-lg font-semibold text-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  Generating Slides...
                </>
              ) : (
                <>
                  <Download className="w-6 h-6" />
                  Generate Slides
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* SharePoint Browser Dialog */}
      <SharePointBrowser
        open={sharePointBrowserOpen}
        onClose={() => setSharePointBrowserOpen(false)}
        onSelect={handleSharePointSelect}
        title={selectionMode === 'file' ? 'Select DOCX Syllabus File' : 'Select Folder with DOCX Files'}
        selectionMode={selectionMode}
        fileFilter={selectionMode === 'file' ? ['.docx'] : []}
        initialPath={{
          siteName: 'FK Partners Portal',
          driveName: 'Conteúdo',
          folderPath: '/1. Objetos de Aprendizagem/000 - apenas SLIDES - Objetos apostilas'
        }}
      />
    </div>
  );
}
